import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Button,
  Input,
  Row,
} from "reactstrap";
import Select from "react-select";
import {
  AsyncClientSelect,
  AsyncSourceSelect,
  AsyncUserSelect,
} from "components/Common/AsyncSearchSelects";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DEFAULT_PAGE_SIZE,
  urlEndPoint,
  pascalToCamel,
  mapApiMetadataToTableColumnDefinition,
} from "helpers/GeneralHelper";
import { toast } from "react-toastify";

import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import AddEditCandidateModal from "pages/AllCommonModal/AddEditCandidateModal/add-edit-candidate-modal";
import DriverDetailsModal from "pages/AllCommonModal/DriverDetailsModal/driver-details-modal";
import CandidateClosureModal from "pages/AllCommonModal/CandidateClosureModal/candidate-closure-modal";
import CandidateMarkAsHiredModal from "pages/AllCommonModal/CandidateMarkAsHiredModal/candidate-mark-as-hired-modal";
import CandidateMarkAsRejectedModal from "pages/AllCommonModal/CandidateMarkAsRejectedModal/candidate-mark-as-rejected-modal";
import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import CandidateListFilterPrompt from "components/Common/CandidateListFilterPrompt";
import {
  getCandidateMetadata,
  candidateTodayCall,
  candidateSureMailSend,
  resetCandidate,
  getCandidateDetailsForUsingCallDetails,
  fetchCommonOrderStatusOptions,
  submitCandidateFilterData,
} from "model/CandidateModel";
import { submitFilterData as submitJobFilterData } from "model/JobModel";
import Swal from "sweetalert2";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import {
  ASYNC_AUTOCOMPLETE_API_TO_KIND,
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";

const ALL_CANDIDATES_PREFILL_STORAGE_KEY = "allCandidatesPrefillFilters";

const readStoredAllCandidatesPrefillFilters = () => {
  try {
    const raw = sessionStorage.getItem(ALL_CANDIDATES_PREFILL_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(ALL_CANDIDATES_PREFILL_STORAGE_KEY);
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const isClientFilterDefinition = (definitionRow = {}) => {
  const asyncKind =
    definitionRow?.asyncKind ||
    ASYNC_AUTOCOMPLETE_API_TO_KIND[definitionRow?.api];
  if (asyncKind === "client") return true;
  return String(definitionRow?.field ?? "").trim().toLowerCase() === "clientid";
};

const resolveClientFilterDefinition = (metadataFilters = []) =>
  (Array.isArray(metadataFilters) ? metadataFilters : []).find(isClientFilterDefinition);

const buildPrefillFilterState = (prefill = {}, emptyFilter = {}, metadataFilters = []) => {
  const clientId = String(prefill?.clientId ?? "").trim();
  const clientLabel = String(
    prefill?.clientIdLabel ?? prefill?.clientName ?? ""
  ).trim();
  const nextFilter = { ...emptyFilter };
  const clientDefinition = resolveClientFilterDefinition(metadataFilters);
  const clientFieldKey = clientDefinition?.field || "clientId";

  if (clientId) {
    nextFilter[clientFieldKey] = clientId;
    nextFilter[getAsyncFilterLabelFieldKey(clientFieldKey)] = clientLabel;
  }

  const profileStatus = String(prefill?.profileStatus ?? "").trim();
  if (profileStatus) {
    nextFilter.profileStatus = profileStatus;
  }

  return nextFilter;
};

const DEFAULT_CANDIDATE_LIST_FILTER = {
  search: "",
  status: "",
  statusId: "",
  orderStatus: "",
  profileStatus: "",
  reason: "",
  clientId: "",
  jobId: "",
  sourceId: "",
  userId: "",
  fromDate: null,
  toDate: null,
  fadvSend: "",
  isFadvSent: "",
  updateSend: "",
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "deactive", label: "Deactive" },
];

const PROFILE_STATUS_OPTIONS = [
  { value: "not started", label: "Not Started" },
  { value: "in progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "existing fedex driver", label: "Existing Fedex Driver" },
  { value: "offer sent", label: "Offer Sent" },
  { value: "hired", label: "Hired" },
  { value: "road test ready", label: "Road Test Ready" },
  { value: "not interested", label: "Not Interested" },
];

const REASON_OPTIONS = [
  { value: "distance", label: "Distance" },
  { value: "pay", label: "Pay" },
  { value: "schedule", label: "Schedule" },
  { value: "other", label: "Other" },
];

const FADV_SEND_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

/** Raw API keys for status columns shown on the candidates table (reset / row patch). */
const CANDIDATE_ROW_STATUS_SOURCE_KEYS = [
  "profileStatus",
  "orderStatus",
  "mecStatus",
  "drugStatus",
  "updateSend",
  "fadvSend",
  "isFadvSent",
  "funnelStatus",
  "funnlStatus",
  "candidateStatus",
  "status",
];

const isDeactiveCandidateRow = (row = {}) => {
  const rawStatus =
    row?._originalCandidate?.candidateStatus ??
    row?._originalCandidate?.status ??
    row?.candidateStatus ??
    row?.status;
  const normalized = String(rawStatus ?? "").trim().toLowerCase();
  return normalized === "deactive" || normalized === "inactive";
};

const isRowInEligibleForHire = (row = {}) => {
  const rawStatus =
    row?._originalCandidate?.profileStatus ??
    row?._originalCandidate?.profileStatus ??
    row?.profileStatus
  return rawStatus === "in-eligible for hire";
};

/** Empty / whitespace-only strings → null for API (no empty strings on filter fields). */
const normalizeOptionalStringFilterValueOrNull = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const trimmed = String(rawValue).trim();
  return trimmed === "" ? null : trimmed;
};

/** Fixed POST keys normalized on every candidates filter request before calling the backend. */
const CANDIDATE_FILTER_POST_BODY_STRING_KEYS_THAT_NORMALIZE_EMPTY_TO_NULL = [
  "nameContains",
  "emailContains",
  "status",
  "orderStatus",
  "profileStatus",
  "reason",
  "clientId",
  "jobId",
  "sourceId",
  "userId",
];

/**
 * Candidate/filter POST body: optional filters as null when not set.
 * Preserves pageNumber, pageSize, and boolean fadvSend / updateSend.
 * `metadataFiltersFromApi` augments normalization so dynamically named filters (metadata `field`)
 * are also stored as JSON null rather than leftover empty strings when cleared.
 */
const normalizeCandidateFilterPayload = (rawCandidateFilterBody = {}, metadataFiltersFromApi = []) => {
  const outboundPayload = { ...rawCandidateFilterBody };

  /** Dynamic field keys from Candidate metadata `/filters`; `search` is handled via email/name contains. */
  const dynamicNormalizeKeysFromMetadata = (
    Array.isArray(metadataFiltersFromApi) ? metadataFiltersFromApi : []
  ).reduce((uniqueKeyAccumulator, filterDefinitionRow) => {
    const backendFieldIdentifier = filterDefinitionRow?.field;
    if (
      backendFieldIdentifier &&
      backendFieldIdentifier !== "search" &&
      !uniqueKeyAccumulator.includes(backendFieldIdentifier)
    ) {
      return [...uniqueKeyAccumulator, backendFieldIdentifier];
    }
    return uniqueKeyAccumulator;
  }, []);

  const normalizedStringKeywordKeysThatShouldBecomeNullWhenBlank = Array.from(
    new Set([
      ...CANDIDATE_FILTER_POST_BODY_STRING_KEYS_THAT_NORMALIZE_EMPTY_TO_NULL,
      ...dynamicNormalizeKeysFromMetadata,
    ])
  );

  normalizedStringKeywordKeysThatShouldBecomeNullWhenBlank.forEach((keywordKeyFromPayload) => {
    if (keywordKeyFromPayload in outboundPayload) {
      outboundPayload[keywordKeyFromPayload] = normalizeOptionalStringFilterValueOrNull(
        outboundPayload[keywordKeyFromPayload]
      );
    }
  });
  if (!outboundPayload.fromDate && outboundPayload.fromDate !== 0) outboundPayload.fromDate = null;
  else if (outboundPayload.fromDate === "") outboundPayload.fromDate = null;
  if (!outboundPayload.toDate && outboundPayload.toDate !== 0) outboundPayload.toDate = null;
  else if (outboundPayload.toDate === "") outboundPayload.toDate = null;

  const fadvSendFilterFromPayload = outboundPayload.fadvSend;
  if (fadvSendFilterFromPayload === true || fadvSendFilterFromPayload === false) {
    outboundPayload.fadvSend = fadvSendFilterFromPayload;
  } else if (fadvSendFilterFromPayload === "true") outboundPayload.fadvSend = true;
  else if (fadvSendFilterFromPayload === "false") outboundPayload.fadvSend = false;
  else outboundPayload.fadvSend = null;

  const updateSendFilterFromPayload = outboundPayload.updateSend;
  if (updateSendFilterFromPayload === true || updateSendFilterFromPayload === false) {
    outboundPayload.updateSend = updateSendFilterFromPayload;
  } else if (updateSendFilterFromPayload === "true") outboundPayload.updateSend = true;
  else if (updateSendFilterFromPayload === "false") outboundPayload.updateSend = false;
  else outboundPayload.updateSend = null;

  Object.keys(outboundPayload).forEach((payloadKey) => {
    if (isAsyncFilterLabelFieldKey(payloadKey)) {
      delete outboundPayload[payloadKey];
    }
  });

  return outboundPayload;
};

const AllCandidates = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { permissionData } = usePermissionContext() || {};
  const prefillHandledRef = useRef(false);
  const pendingPrefillRef = useRef(null);
  /** True after Search / modal submit / prefill — skips filter API on first paint and on Reset. */
  const [candidateFilterApplied, setCandidateFilterApplied] = useState(false);
  const [toolbarFilterRemountKey, setToolbarFilterRemountKey] = useState(0);

  if (!pendingPrefillRef.current) {
    const fromNavigation = location.state?.prefillFilters;
    const fromStorage = readStoredAllCandidatesPrefillFilters();
    pendingPrefillRef.current =
      (fromNavigation && typeof fromNavigation === "object" ? fromNavigation : null) ||
      fromStorage;
  }

  useEffect(() => {
    const fromNavigation = location.state?.prefillFilters;
    if (!fromNavigation || typeof fromNavigation !== "object") return;
    const clientId = String(fromNavigation?.clientId ?? "").trim();
    if (!clientId) return;
    pendingPrefillRef.current = fromNavigation;
    prefillHandledRef.current = false;
  }, [location.state]);
  /** `getDropdownOptionsByApiKey`: loads metadata `dropdown`/`api` options for the toolbar (same endpoints as AllCommonFilterModal). */
  const { fetchMetadataSchema, getDropdownOptionsByApiKey } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("Candidates");

  const [candidateListFilter, setCandidateListFilter] = useState({
    ...DEFAULT_CANDIDATE_LIST_FILTER,
  });
  const [candidateData, setCandidateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    emailContains: null,
    status: null,
    orderStatus: null,
    profileStatus: null,
    reason: null,
    clientId: null,
    jobId: null,
    sourceId: null,
    userId: null,
    fromDate: null,
    toDate: null,
    fadvSend: null,
    updateSend: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [candidateFilterMetaData, setCandidateFilterMetaData] = useState([]);

  const [jobOptions, setJobOptions] = useState([]);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  const [candidateEditOpen, setCandidateEditOpen] = useState(false);
  const [candidateForEdit, setCandidateForEdit] = useState(null);
  const [driverDetailsOpen, setDriverDetailsOpen] = useState(false);
  const [driverDetailsCandidateId, setDriverDetailsCandidateId] = useState(null);
  const [closureModalOpen, setClosureModalOpen] = useState(false);
  const [closureCandidateId, setClosureCandidateId] = useState(null);
  const [markAsHiredModalOpen, setMarkAsHiredModalOpen] = useState(false);
  const [markAsHiredCandidateId, setMarkAsHiredCandidateId] = useState(null);
  const [markAsRejectedModalOpen, setMarkAsRejectedModalOpen] = useState(false);
  const [markAsRejectedCandidateId, setMarkAsRejectedCandidateId] = useState(null);

  /** Option lists for toolbar `dropdown` filters (lazy-loaded via CommonFilterOptionsContext map). */
  const userData = localStorage.getItem("userData");

  const {
    metadataFiltersAll,
    metadataToolbarFilterDefinitions: candidateMetadataToolbarFilterDefinitions,
    metadataModalOnlyFilterDefinitions: candidateMetadataModalOnlyFilterDefinitions,
    usesMetadataDrivenFilterUi: usesMetadataDrivenCandidateFilterUi,
    shouldShowTableHeaderFilterAndResetControls,
    shouldRenderMetadataToolbarFilterRow: shouldRenderCandidateMetadataToolbarFilterRow,
    modalFilterData: metadataDrivenModalFilterData,
    activeModalFilterCount,
    buildBaselineEmptyListFilter,
    toolbarDropdownReactSelectOptionsByFieldName,
  } = useMetadataDrivenFilters(
    candidateFilterMetaData,
    candidateListFilter,
    "Candidates"
  );

  const buildBaselineEmptyCandidateFilterState = useCallback(() => {
    const baselineCandidateFilterState = {
      ...DEFAULT_CANDIDATE_LIST_FILTER,
      ...buildBaselineEmptyListFilter(),
    };
    return baselineCandidateFilterState;
  }, [buildBaselineEmptyListFilter]);

  /** Stable filter config for AllCommonFilterModal — metadata modal slice, or the legacy static field list. */
  const allCandidatesFilterModalData = useMemo(
    () => {
      if (candidateMetadataModalOnlyFilterDefinitions.length) {
        return metadataDrivenModalFilterData;
      }

      if (usesMetadataDrivenCandidateFilterUi) {
        return [];
      }

      return [];
    },
    [
      candidateMetadataModalOnlyFilterDefinitions,
      metadataDrivenModalFilterData,
      usesMetadataDrivenCandidateFilterUi,
    ]
  );

  const defaultTableColumns = useMemo(
    () => [
      { Header: "Client", accessor: "client" },
      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "Phone", accessor: "phone" },
      { Header: "Created On", accessor: "createdOn" },
      { Header: "Job", accessor: "job" },
      { Header: "Profile Status", accessor: "profileStatus" },
      { Header: "Order Status", accessor: "orderStatus" },
      { Header: "Source", accessor: "source" },
      { Header: "Status", accessor: "status" },
    ],
    []
  );

  /** Ensure every backend filter key exists inside local React state once metadata resolves (controlled inputs everywhere). */
  useEffect(() => {
    if (!Array.isArray(candidateFilterMetaData) || !candidateFilterMetaData.length) return;
    setCandidateListFilter((previousCandidateFilterState) => {
      const mergedCandidateFilterState = { ...previousCandidateFilterState };
      let mergedNewKeysIntoState = false;
      candidateFilterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier) return;
        if (!(backendFilterFieldIdentifier in mergedCandidateFilterState)) {
          mergedCandidateFilterState[backendFilterFieldIdentifier] =
            filterDefinitionFromMetadataApi?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionFromMetadataApi)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(backendFilterFieldIdentifier);
          if (!(asyncLabelFieldKey in mergedCandidateFilterState)) {
            mergedCandidateFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedCandidateFilterState : previousCandidateFilterState;
    });
  }, [candidateFilterMetaData]);

  /** Converts job (or generic id/title rows) API responses into `{ value, label }` react-select primitives. */
  const mapApiRecordsToLabelValueSelectOptions = (
    candidateJobRecordRows = [],
    preferredLabelAttributeKeysDescendingPriority = []
  ) => {
    const normalizedRecordSourceRows = Array.isArray(candidateJobRecordRows)
      ? candidateJobRecordRows
      : [];
    return normalizedRecordSourceRows
      .map((singleRecordRow) => {
        const canonicalValuePrimaryKey =
          singleRecordRow?.id || singleRecordRow?._id || "";
        if (!canonicalValuePrimaryKey) return null;
        const humanReadableDisplayLabel =
          preferredLabelAttributeKeysDescendingPriority
            .map((attributeAccessorKeyOnRow) => singleRecordRow?.[attributeAccessorKeyOnRow])
            .find((potentialLabelCell) => String(potentialLabelCell || "").trim()) ||
          singleRecordRow?.name ||
          singleRecordRow?.title ||
          canonicalValuePrimaryKey;
        return {
          value: canonicalValuePrimaryKey,
          label: humanReadableDisplayLabel,
        };
      })
      .filter(Boolean);
  };

  const mapRecordsToRows = (list = []) =>
    list.map((item = {}) => ({
      ...item,
      _originalCandidate: { ...item },
      id: item?.id || item?.candidateId || "",
    }));

  /** Translate the on-page filter model (toolbar + drawer) into the POST body Candidate filter endpoint expects. */
  const buildPayloadFromFilterForm = (form = {}, overrides = {}) => {
    const searchKeywordTrimmed = String(form?.search || "").trim();
    const clientFilterDefinition = resolveClientFilterDefinition(candidateFilterMetaData);
    const clientFieldKey = clientFilterDefinition?.field || "clientId";
    const fromDateForFilter = normalizeMetadataFilterDateForApi(form?.fromDate);
    const toDateForFilter = normalizeMetadataFilterDateForApi(form?.toDate);
    const statusOrStatusIdentifierValueFromForm =
      form?.status ?? form?.statusId ?? null;
    let fadvSentBooleanOrNullPayloadValue = form?.fadvSend ?? form?.isFadvSent;
    if (fadvSentBooleanOrNullPayloadValue === "true") {
      fadvSentBooleanOrNullPayloadValue = true;
    } else if (fadvSentBooleanOrNullPayloadValue === "false") {
      fadvSentBooleanOrNullPayloadValue = false;
    } else {
      fadvSentBooleanOrNullPayloadValue = null;
    }

    let updateSendBooleanOrNullPayloadValue = form?.updateSend;
    if (updateSendBooleanOrNullPayloadValue === "true") {
      updateSendBooleanOrNullPayloadValue = true;
    } else if (updateSendBooleanOrNullPayloadValue === "false") {
      updateSendBooleanOrNullPayloadValue = false;
    } else {
      updateSendBooleanOrNullPayloadValue = null;
    }

    /**
     * First copy every backend-declared filter field verbatim so dynamically named IDs (beyond the legacy constants)
     * still reach `/Candidate/submit`-style handlers; canonical keys below intentionally override overlaps.
     */
    const looselyMappedMetadataFilterValuesFromUiFormLayer = {};
    if (Array.isArray(candidateFilterMetaData)) {
      candidateFilterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier || backendFilterFieldIdentifier === "search") return;
        looselyMappedMetadataFilterValuesFromUiFormLayer[backendFilterFieldIdentifier] =
          form?.[backendFilterFieldIdentifier];
      });
    }

    return normalizeCandidateFilterPayload(
      {
        ...looselyMappedMetadataFilterValuesFromUiFormLayer,
        pageNumber: 1,
        pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
        nameContains: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        emailContains: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        status:
          normalizeOptionalStringFilterValueOrNull(statusOrStatusIdentifierValueFromForm),
        orderStatus:
          normalizeOptionalStringFilterValueOrNull(form?.orderStatus),
        profileStatus:
          normalizeOptionalStringFilterValueOrNull(form?.profileStatus),
        reason: normalizeOptionalStringFilterValueOrNull(form?.reason),
        clientId: normalizeOptionalStringFilterValueOrNull(
          form?.[clientFieldKey] ?? form?.clientId
        ),
        jobId: normalizeOptionalStringFilterValueOrNull(form?.jobId),
        sourceId:
          normalizeOptionalStringFilterValueOrNull(form?.sourceId),
        userId:
          normalizeOptionalStringFilterValueOrNull(form?.userId),
        fromDate: fromDateForFilter,
        toDate: toDateForFilter,
        fadvSend: fadvSentBooleanOrNullPayloadValue,
        updateSend: updateSendBooleanOrNullPayloadValue,
        ...overrides,
      },
      candidateFilterMetaData
    );
  };

  const getCandidateList = async (payloadOverrides = {}, options = {}) => {
    const { onlyPagination = false } = options;
    setLoading(true);
    try {
      const payload = onlyPagination
        ? {
            pageNumber: Number(payloadOverrides.pageNumber) || 1,
            pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
          }
        : normalizeCandidateFilterPayload(
            {
              ...requestPayload,
              ...payloadOverrides,
            },
            candidateFilterMetaData
          );

      const response = await submitCandidateFilterData(payload);
      const responseData = response?.data ?? response ?? {};
      const listData =
        responseData?.records ??
        responseData?.data ??
        (Array.isArray(responseData) ? responseData : []);

      const records = Array.isArray(listData) ? listData : [];
      setCandidateData(mapRecordsToRows(records));

      if (response == null || response?.success !== false) {
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });
        if (onlyPagination) {
          const clearedServerFilterSnapshotAfterPaginationReset =
            normalizeCandidateFilterPayload(
              {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                nameContains: null,
                emailContains: null,
                status: null,
                orderStatus: null,
                profileStatus: null,
                reason: null,
                clientId: null,
                jobId: null,
                sourceId: null,
                userId: null,
                fromDate: null,
                toDate: null,
                fadvSend: null,
                updateSend: null,
                ...Object.fromEntries(
                  (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : [])
                    .filter(
                      (filterDefinitionFromMetadataApi) =>
                        filterDefinitionFromMetadataApi?.field &&
                        filterDefinitionFromMetadataApi.field !== "search"
                    )
                    .map((filterDefinitionFromMetadataApi) => [
                      filterDefinitionFromMetadataApi.field,
                      null,
                    ])
                ),
              },
              candidateFilterMetaData
            );
          setRequestPayload(clearedServerFilterSnapshotAfterPaginationReset);
          setCandidateListFilter(buildBaselineEmptyCandidateFilterState());
        } else {
          setRequestPayload(
            normalizeCandidateFilterPayload(payload, candidateFilterMetaData)
          );
        }
      } else {
        setCandidateData([]);
        if (response) {
          toast.error(response?.message || "Unable to load candidates");
        }
      }
    } catch (err) {
      toast.error(err?.message || "Unable to load candidates");
      setCandidateData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterDropdowns = async () => {
    try {
      const [jobRes, orderStatusRes] = await Promise.all([
        userData && userData?.userTypeId===1 &&  submitJobFilterData({}),
        fetchCommonOrderStatusOptions(),
      ]);
      const jobRecords = jobRes?.data?.records || jobRes?.data || [];
      const orderStatusRecords = orderStatusRes?.data || [];
      setJobOptions(
        mapApiRecordsToLabelValueSelectOptions(jobRecords, ["name", "jobName", "title"])
      );
      setOrderStatusOptions(
        (Array.isArray(orderStatusRecords) ? orderStatusRecords : [])
          .map((item) => ({
            value: String(item?.value || "").trim(),
            label: item?.data || item?.value || "Order Status",
          }))
          .filter((opt) => opt?.value)
      );
    } catch (e) {
      toast.error(e?.message || "Unable to load filter lists");
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      const skipDefaultListLoad = Boolean(
        String(pendingPrefillRef.current?.clientId ?? "").trim()
      );
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:candidate", () => getCandidateMetadata());
        const meta = metaRes?.data ?? metaRes ?? {};
        const metadataFilters = Array.isArray(meta?.filters) ? meta.filters : [];

        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => {
              const accessor = pascalToCamel(col?.field);
              if (accessor === "candidateStatus") {
                return mapApiMetadataToTableColumnDefinition(col, {
                  resolveAccessor: () => "status",
                });
              }
              return mapApiMetadataToTableColumnDefinition(col);
            })
          : defaultTableColumns;
        setTableMetaData(tableColumns);
        setCandidateFilterMetaData(metadataFilters);
      } catch (e) {
        toast.error(e?.message || "Unable to load candidate metadata");
        setTableMetaData(defaultTableColumns);
        setCandidateFilterMetaData([]);
      }

      await fetchFilterDropdowns();
      if (!skipDefaultListLoad || !prefillHandledRef.current) {
        setLoading(false);
      }
    };
    bootstrap();
  }, []);

  /** Apply settings page size once ready (before any filter API call). */
  useEffect(() => {
    if (!settingsReady) return;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) =>
      previous.pageSize === pageSize ? previous : { ...previous, pageSize }
    );
    setPaginationMeta((previous) =>
      previous.pageSize === pageSize ? previous : { ...previous, pageSize }
    );
  }, [settingsReady, settingsPageSize]);

  useEffect(() => {
    const prefill = pendingPrefillRef.current;
    const clientId = String(prefill?.clientId ?? "").trim();
    if (!clientId || prefillHandledRef.current) return;
    if (!settingsReady) return;
    if (!Array.isArray(candidateFilterMetaData) || !candidateFilterMetaData.length) return;

    prefillHandledRef.current = true;
    setCandidateFilterApplied(true);
    const emptyFilter = buildBaselineEmptyCandidateFilterState();
    const mergedFilter = buildPrefillFilterState(
      prefill,
      emptyFilter,
      candidateFilterMetaData
    );
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;

    setCandidateListFilter(mergedFilter);
    setToolbarFilterRemountKey((previousKey) => previousKey + 1);
    void getCandidateList(
      buildPayloadFromFilterForm(mergedFilter, { pageSize })
    );
    pendingPrefillRef.current = null;
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    settingsReady,
    settingsPageSize,
    candidateFilterMetaData,
    buildBaselineEmptyCandidateFilterState,
    navigate,
    location.pathname,
  ]);

  /**
   * Modal submit should only replace modal-owned fields so toolbar defaults (e.g. `search`) continue to apply
   * until the user edits them — mirroring how verify-test keeps its upper row independent.
   */
  const handleAllCandidatesFilterSubmit = async (filterFormValuesFromModal = {}) => {
    const mergedCandidateListFilterAfterModalSave = mergeModalSubmitIntoListFilter(
      candidateListFilter,
      allCandidatesFilterModalData,
      filterFormValuesFromModal
    );
    setCandidateListFilter(mergedCandidateListFilterAfterModalSave);
    setCommonFilterModalOpen(false);
    setCandidateFilterApplied(true);
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    const nextServerFilterPayload = buildPayloadFromFilterForm(
      mergedCandidateListFilterAfterModalSave,
      { pageSize }
    );
    await getCandidateList(nextServerFilterPayload);
  };

  const handleAllCandidatesFilterReset = async () => {
    setCandidateFilterApplied(false);
    const resetCandidateFilterStateMatchingMetadataKeys = buildBaselineEmptyCandidateFilterState();
    setCandidateListFilter(resetCandidateFilterStateMatchingMetadataKeys);
    setCommonFilterModalOpen(false);
    setCandidateData([]);
    setPaginationMeta({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      totalPages: 0,
      totalRecords: 0,
    });
    setRequestPayload(
      normalizeCandidateFilterPayload(
        {
          pageNumber: 1,
          pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
          nameContains: null,
          emailContains: null,
          status: null,
          orderStatus: null,
          profileStatus: null,
          reason: null,
          clientId: null,
          jobId: null,
          sourceId: null,
          userId: null,
          fromDate: null,
          toDate: null,
          fadvSend: null,
          updateSend: null,
          ...Object.fromEntries(
            (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : [])
              .filter(
                (filterDefinitionFromMetadataApi) =>
                  filterDefinitionFromMetadataApi?.field &&
                  filterDefinitionFromMetadataApi.field !== "search"
              )
              .map((filterDefinitionFromMetadataApi) => [
                filterDefinitionFromMetadataApi.field,
                null,
              ])
          ),
        },
        candidateFilterMetaData
      )
    );
  };

  /** Toolbar Search button / Enter key: push the current on-page filter object to the server without opening the modal. */
  const handleUpperToolbarCandidateFilterSearch = async () => {
    setCandidateFilterApplied(true);
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    const nextServerFilterPayloadFromToolbar = buildPayloadFromFilterForm(
      candidateListFilter,
      { pageSize }
    );
    await getCandidateList(nextServerFilterPayloadFromToolbar);
  };

  const handleToolbarTextFilterInputChange = (backendFilterFieldKey, nextInputStringValue) => {
    setCandidateListFilter((previousCandidateFilterState) => ({
      ...previousCandidateFilterState,
      [backendFilterFieldKey]: nextInputStringValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (backendFilterFieldKey, nextSelectedOption) => {
    setCandidateListFilter((previousCandidateFilterState) => ({
      ...previousCandidateFilterState,
      [backendFilterFieldKey]: nextSelectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(backendFilterFieldKey)]:
        nextSelectedOption?.label ?? "",
    }));
  };

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleAllCandidatesFilterReset();
    };
    run();
  }, [commonResetFilter]);

  const handlePageChange = async (page) => {
    if (!candidateFilterApplied) return;
    await getCandidateList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    if (!candidateFilterApplied) return;
    await getCandidateList({ pageNumber: 1, pageSize: rows });
  };

  const handleCandidateEdit = (row) => {
    const raw = row?._originalCandidate ?? row;
    setCandidateForEdit(raw && typeof raw === "object" ? { ...raw } : {});
    setCandidateEditOpen(true);
  };

  const closeCandidateEditModal = () => {
    setCandidateEditOpen(false);
    setCandidateForEdit(null);
  };

  const resolveCandidateRowId = (row = {}) =>
    String(row?.id ?? row?._id ?? row?.candidateId ?? "").trim();

  /** Merge API/form updates into one visible row without re-running the filter POST. */
  const applyCandidateRowPatch = useCallback((candidateId, partialRawUpdate = {}) => {
    const targetId = String(candidateId ?? "").trim();
    if (!targetId || !partialRawUpdate || typeof partialRawUpdate !== "object") return;
    setCandidateData((previousRows) => {
      let rowMatched = false;
      const nextRows = previousRows.map((tableRow) => {
        if (resolveCandidateRowId(tableRow) !== targetId) return tableRow;
        rowMatched = true;
        const mergedRawCandidateRecord = {
          ...(tableRow._originalCandidate || {}),
          ...partialRawUpdate,
          id: partialRawUpdate?.id ?? tableRow._originalCandidate?.id ?? targetId,
          candidateId:
            partialRawUpdate?.candidateId ??
            partialRawUpdate?.id ??
            tableRow._originalCandidate?.candidateId ??
            targetId,
        };
        const [mappedTableRow] = mapRecordsToRows([mergedRawCandidateRecord]);
        return mappedTableRow;
      });
      return rowMatched ? nextRows : previousRows;
    });
  }, []);

  const handleCandidateEditSuccess = useCallback(
    (updatedRawCandidate) => {
      if (!updatedRawCandidate || typeof updatedRawCandidate !== "object") return;
      const targetId = String(
        updatedRawCandidate?.id ?? updatedRawCandidate?.candidateId ?? ""
      ).trim();
      if (!targetId) return;
      applyCandidateRowPatch(targetId, updatedRawCandidate);
    },
    [applyCandidateRowPatch]
  );

  /** Remove deactivated rows locally and adjust total count. */
  const handleDeactivateCandidatesSuccess = useCallback((deactivatedIds = []) => {
    const deactivatedIdSet = new Set(
      (Array.isArray(deactivatedIds) ? deactivatedIds : [])
        .map((id) => String(id).trim())
        .filter(Boolean)
    );
    if (!deactivatedIdSet.size) return;
    setCandidateData((previousRows) => {
      const nextRows = previousRows.filter(
        (tableRow) => !deactivatedIdSet.has(resolveCandidateRowId(tableRow))
      );
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount > 0) {
        setPaginationMeta((previousPagination) => ({
          ...previousPagination,
          totalRecords: Math.max(
            0,
            (Number(previousPagination?.totalRecords) || 0) - removedCount
          ),
        }));
      }
      return nextRows;
    });
  }, []);

  const extractCandidatePatchFromApiResponse = (response) => {
    const responseData = response?.data;
    if (!responseData || typeof responseData !== "object") return {};
    if (Array.isArray(responseData)) {
      const firstRecord = responseData[0];
      return firstRecord && typeof firstRecord === "object" ? firstRecord : {};
    }
    if (responseData.record && typeof responseData.record === "object") {
      return responseData.record;
    }
    if (responseData.candidate && typeof responseData.candidate === "object") {
      return responseData.candidate;
    }
    if (Array.isArray(responseData.records) && responseData.records[0]) {
      return responseData.records[0];
    }
    return responseData;
  };

  const candidateRowPatchIncludesStatusFields = (patch = {}) =>
    CANDIDATE_ROW_STATUS_SOURCE_KEYS.some(
      (fieldKey) => patch[fieldKey] !== undefined && patch[fieldKey] !== null
    );

  const fetchCandidateRowPatchById = async (candidateId) => {
    const response = await getCandidateDetailsForUsingCallDetails({
      candidateId,
    });
    const rowData = response?.data ?? response;
    if (!rowData || typeof rowData !== "object" || Array.isArray(rowData)) return null;
    const id = String(candidateId ?? "").trim();
    return {
      ...rowData,
      id: rowData?.id ?? id,
      candidateId: rowData?.candidateId ?? id,
    };
  };

  const handleCandidateAddClosure = (row) => {
    const cid = resolveCandidateRowId(row);
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }
    setClosureCandidateId(cid);
    setClosureModalOpen(true);
  };

  const closeClosureModal = () => {
    setClosureModalOpen(false);
    setClosureCandidateId(null);
  };

  const handleCandidateMarkAsHired = (row) => {
    const cid = resolveCandidateRowId(row);
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }
    setMarkAsHiredCandidateId(cid);
    setMarkAsHiredModalOpen(true);
  };

  const closeMarkAsHiredModal = () => {
    setMarkAsHiredModalOpen(false);
    setMarkAsHiredCandidateId(null);
  };

  const handleCandidateMarkAsRejected = (row) => {
    const cid = resolveCandidateRowId(row);
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }
    setMarkAsRejectedCandidateId(cid);
    setMarkAsRejectedModalOpen(true);
  };

  const closeMarkAsRejectedModal = () => {
    setMarkAsRejectedModalOpen(false);
    setMarkAsRejectedCandidateId(null);
  };

  const handleTodayCall = async (row) => {
    const id = resolveCandidateRowId(row);
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Add today’s call?",
      text: "Are you sure you want to add today’s call for this candidate?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, add",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const response = await candidateTodayCall(id);
    if (response?.success || response?.isSuccess) {
      toast.success(response?.message || "Today’s call updated");
      const patchFromApi = extractCandidatePatchFromApiResponse(response);
      if (Object.keys(patchFromApi).length) {
        applyCandidateRowPatch(id, { id, candidateId: id, ...patchFromApi });
      }
      return;
    }
    toast.error(response?.message || "Unable to add today’s call");
  };

  const handleSureMailSend = async (row) => {
    const id = resolveCandidateRowId(row);
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Reset mail send?",
      text: "Are you want to reset mail send?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, reset",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const response = await candidateSureMailSend(id);
    if (response?.success || response?.isSuccess) {
      toast.success(response?.message || "Mail sent");
      return;
    }
    // toast.error(response?.message || "Unable to reset mail");
  };

  const handleResetCandidate = async (row) => {
    const id = resolveCandidateRowId(row);
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Reset Candidate?",
      text: "Are you want to Reset this candidate?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const response = await resetCandidate(id);
    if (response?.success || response?.isSuccess) {
      toast.success(response?.message);
      let rowPatch = { id, candidateId: id, ...extractCandidatePatchFromApiResponse(response) };
      if (!candidateRowPatchIncludesStatusFields(rowPatch)) {
        const fetchedRow = await fetchCandidateRowPatchById(id);
        if (fetchedRow) {
          rowPatch = { ...fetchedRow, ...rowPatch };
        }
      }
      applyCandidateRowPatch(id, rowPatch);
      return;
    }
    // toast.error(response?.message || "Unable to reset candidate");
  };

  const handleDriverDetails = (row) => {
    const candidateId = row?.candidateId;
    if (!candidateId) {
      toast.error("Candidate ID not found");
      return;
    }
    setDriverDetailsCandidateId(String(candidateId));
    setDriverDetailsOpen(true);
  };

  const closeDriverDetailsModal = () => {
    setDriverDetailsOpen(false);
    setDriverDetailsCandidateId(null);
  };

  /** Refresh list after bulk transfer so client columns reflect the new client. */
  const handleBulkTransferCandidatesSuccess = useCallback(() => {
    if (!candidateFilterApplied) return;
    void getCandidateList({ pageNumber: paginationMeta?.pageNumber || 1 });
  }, [candidateFilterApplied, paginationMeta?.pageNumber]);

  const handleMoveToAiRecruiterScheduleSuccess = useCallback(() => {
    if (!candidateFilterApplied) return;
    void getCandidateList({ pageNumber: paginationMeta?.pageNumber || 1 });
  }, [candidateFilterApplied, paginationMeta?.pageNumber]);

  const filteredData = useMemo(() => candidateData, [candidateData]);
  const candidateActionPermissions = useMemo(
    () => ({
      canEdit: Boolean(permissionData?.CandidateUpdateAccess),
      canCallDetails: Boolean(
        permissionData?.CallCallDetailsAccess || permissionData?.CallDetailsAccess
      ),
      canDriverDetails: Boolean(permissionData?.CandidateDriverDetailsAccess),
      canTodayCall: Boolean(permissionData?.CandidateAddTodayAccess),
      canSureMailSend: Boolean(permissionData?.CandidateResetMailSend),
      canResetCandidate: Boolean(permissionData?.CandidateResetAccess),
      canAddClosure: Boolean(permissionData?.CandidateModifyClosureAccess),
      canMarkAsHired: Boolean(permissionData?.CandidateHiredAccess),
      canMarkAsRejected: Boolean(permissionData?.CandidateRejectedAccess),
      canBulkDeactivate: Boolean(permissionData?.CandidateDeactiveMultipleAccess),
      canBulkTransfer: Boolean(permissionData?.CandidateUpdateAccess),
      canMoveToAiRecruiterSchedule: Boolean(
        permissionData?.AIRecruiterAccess || permissionData?.AIRecruiterLogAccess
      ),
    }),
    [permissionData]
  );
  const hasAnyCandidateRowAction = useMemo(() => {
    const { canBulkDeactivate, canBulkTransfer, canMoveToAiRecruiterSchedule, ...rowActions } =
      candidateActionPermissions;
    return Object.values(rowActions).some(Boolean);
  }, [candidateActionPermissions]);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">Candidates</h5>
            </div>
            <hr />

            {/**
             * Compact filter row driven solely by metadata `isDefault: true` entries (UX alignment with verify-test.jsx).
             */}
            {shouldRenderCandidateMetadataToolbarFilterRow ? (
              <MetadataFilterToolbar
                remountKey={toolbarFilterRemountKey}
                toolbarDefinitions={candidateMetadataToolbarFilterDefinitions}
                listFilter={candidateListFilter}
                onTextChange={handleToolbarTextFilterInputChange}
                onSelectChange={handleToolbarReactSelectFilterChange}
                onSearch={handleUpperToolbarCandidateFilterSearch}
                onClear={handleAllCandidatesFilterReset}
                showClearWhenNoModalFilters={
                  !shouldShowTableHeaderFilterAndResetControls
                }
                toolbarDropdownOptionsByFieldName={
                  toolbarDropdownReactSelectOptionsByFieldName
                }
                layoutPreset="candidates"
                pageName="AllCandidates"
              />
            ) : null}

            {loading ? (
             <Loaders />
            ) : !tableMetaData?.length ? (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            ) : (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={candidateFilterApplied ? filteredData : []}
                emptyTableContent={
                  !candidateFilterApplied ? (
                    <CandidateListFilterPrompt
                      variant="table"
                      title="Apply filters to view candidates"
                      description="To load candidate records, choose your filter criteria above and click Search. Your results will appear in this table."
                      onSearch={handleUpperToolbarCandidateFilterSearch}
                    />
                  ) : undefined
                }
                getRowClassName={(row) =>
                  isRowInEligibleForHire(row) ? 'candidate-row-ineligible-for-hire' : ""
                }
              isGlobalFilter={false}
              isSort={true}
              isCheckboxLeftSide={
                candidateActionPermissions.canBulkDeactivate ||
                candidateActionPermissions.canBulkTransfer ||
                candidateActionPermissions.canMoveToAiRecruiterSchedule
              }
              enableBulkDeactivate={candidateActionPermissions.canBulkDeactivate}
              enableBulkTransfer={candidateActionPermissions.canBulkTransfer}
              enableMoveToAiRecruiterSchedule={
                candidateActionPermissions.canMoveToAiRecruiterSchedule
              }
              isActionRequired={hasAnyCandidateRowAction}
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light"
              getDetailsHref={
                candidateActionPermissions.canCallDetails
                  ? (row) => {
                      const id = resolveCandidateRowId(row);
                      return id ? `/${urlEndPoint.callDetails}/${id}` : "";
                    }
                  : undefined
              }
                onEdit={
                  candidateActionPermissions.canEdit ? handleCandidateEdit : undefined
                }
                onAddClosure={
                  candidateActionPermissions.canAddClosure
                    ? handleCandidateAddClosure
                    : undefined
                }
                onMarkAsHired={
                  candidateActionPermissions.canMarkAsHired
                    ? handleCandidateMarkAsHired
                    : undefined
                }
                onMarkAsRejected={
                  candidateActionPermissions.canMarkAsRejected
                    ? handleCandidateMarkAsRejected
                    : undefined
                }
                onTodayCall={
                  candidateActionPermissions.canTodayCall ? handleTodayCall : undefined
                }
                onSureMailSend={
                  candidateActionPermissions.canSureMailSend ? handleSureMailSend : undefined
                }
                onResetCandidate={
                  candidateActionPermissions.canResetCandidate
                    ? handleResetCandidate : undefined
                }
                onDriverDetails={
                  candidateActionPermissions.canDriverDetails
                    ? handleDriverDetails
                    : undefined
                }
                pageName="Candidates"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
              setCommonFilterModalOpen={setCommonFilterModalOpen}
              setCommonResetFilter={setCommonResetFilter}
                allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                activeFilterCount={activeModalFilterCount}
                onDeactivateSuccess={handleDeactivateCandidatesSuccess}
                onTransferSuccess={handleBulkTransferCandidatesSuccess}
                onMoveToAiRecruiterScheduleSuccess={handleMoveToAiRecruiterScheduleSuccess}
            />
            )}
          </CardBody>
        </Card>

        <AllCommonFilterModal
          setCommonFilterModalOpen={setCommonFilterModalOpen}
          commonFilterModalOpen={commonFilterModalOpen}
          modalTitle="Candidates Filter"
          handleSubmit={handleAllCandidatesFilterSubmit}
          filterData={allCandidatesFilterModalData}
          metadataFilterDefinitions={metadataFiltersAll}
          enableFilterToolbarCustomization={usesMetadataDrivenCandidateFilterUi}
          commonResetFilter={commonResetFilter}
          setCommonResetFilter={setCommonResetFilter}
          pageName="Candidates"
        />

        <AddEditCandidateModal
          isOpen={candidateEditOpen}
          onClose={closeCandidateEditModal}
          mode="edit"
          candidate={candidateForEdit || {}}
          onSuccess={handleCandidateEditSuccess}
        />

        <DriverDetailsModal
          isOpen={driverDetailsOpen}
          onClose={closeDriverDetailsModal}
          candidateId={driverDetailsCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />

        <CandidateClosureModal
          key={closureCandidateId || "candidate-closure"}
          isOpen={closureModalOpen}
          onClose={closeClosureModal}
          candidateId={closureCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />

        <CandidateMarkAsHiredModal
          isOpen={markAsHiredModalOpen}
          onClose={closeMarkAsHiredModal}
          candidateId={markAsHiredCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />

        <CandidateMarkAsRejectedModal
          isOpen={markAsRejectedModalOpen}
          onClose={closeMarkAsRejectedModal}
          candidateId={markAsRejectedCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />
      </Container>
    </div>
  );
};

AllCandidates.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(AllCandidates);
